import React, { useState, useEffect } from 'react';
import PhoneFilters from '../components/PhoneFilters';
import { useShares, useExportShares } from '../hooks/useShares';
import { useIsAdmin } from '../hooks/useIsAdmin.ts';
import { useGetProfile } from '../hooks/useProfile.ts';
import { useMembers } from '../hooks/useMembers.ts';
import { 
  Plus, 
  Search, 
  Clock, 
  CheckCircle, 
  XCircle, 
  HelpCircle,
  Filter,
  RefreshCw,
  User,
  Calendar,
  FileText,
  Edit,
  Trash2,
  Eye,
  TrendingUp,
  DollarSign,
  Users,
  Award,
  BarChart3,
  Download
} from 'lucide-react';
import toast from 'react-hot-toast';
import { formatCurrency } from '../hooks/useLoanRepayments.ts';
import Select from '../components/ui/Select.tsx';
import getApiErrorMessage from '../utils/helper.js';
import { PermissionGate } from '../components/PermissionGate';
import { usePermissions } from '../hooks/usePermissions';
import { PERMISSIONS } from '../utils/permissions';

const SharesPage = () => {
  const { can } = usePermissions();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedShare, setSelectedShare] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [newShare, setNewShare] = useState({
    member_id: '',
    amount: '',
    notes: ''
  });
  const [editShare, setEditShare] = useState({
    id: '',
    member_id: '',
    amount: '',
    notes: ''
  });

  const isAdmin = useIsAdmin();
  const { data: userProfile } = useGetProfile();
  const { data: membersData, isLoading: isLoadingMembers } = useMembers();

  // Create member options for react-select
  const memberOptions = membersData?.data?.map(m => ({ value: m.id, label: `${m.name} (${m.phone_number})` })) || [];

  // Debug logging
  console.log('🔍 SharesPage Debug Info:');
  console.log('Is Admin:', isAdmin);
  console.log('User Profile:', userProfile);
  console.log('Profile Data:', userProfile?.data);
  console.log('Is Group Admin:', userProfile?.data?.is_group_admin);

  // Get member id for non-admin
  const memberId = !isAdmin ? (userProfile?.member?.id || userProfile?.id) : undefined;

  const { 
    shares, 
    isLoading, 
    error, 
    refetch,
    updateShareStatus,
    addShare,
    updateShare,
    deleteShare,
    memberShares,
    isLoadingMemberShares,
    statistics,
    isLoadingStatistics
  } = useShares({
    search: searchQuery,
    status: selectedStatus,
    per_page: 1000,
    member_id: memberId
  });

  const exportShares = useExportShares();

  const members = membersData?.data || [];

  const filteredShares = shares.filter((item) => {
    const memberName = item.member?.name?.toLowerCase() || '';
    const memberPhone = item.member?.phone_number || '';
    const matchesSearch = memberName.includes(searchQuery.toLowerCase()) ||
                         memberPhone.includes(searchQuery) ||
                         item.amount?.toString().includes(searchQuery);
    const matchesStatus = selectedStatus === 'all' || item.status === selectedStatus;
    return matchesSearch && matchesStatus;
  });

  const handleStatusUpdate = async (status) => {
    if (!selectedShare) return;
    
    try {
      await updateShareStatus.mutateAsync({
        share_id: selectedShare.id,
        status,
      });
      setShowModal(false);
      setSelectedShare(null);
      toast.success(`Share ${status} successfully`);
    } catch (error) {
      toast.error(error.message || 'Failed to update status');
    }
  };

  const handleAddShare = async () => {
    if (!newShare.member_id || !newShare.amount) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      await addShare({
        member_id: parseInt(newShare.member_id),
        amount: parseFloat(newShare.amount),
        notes: newShare.notes
      });
      setShowAddModal(false);
      setNewShare({ member_id: '', amount: '', notes: '' });
      toast.success('Share added successfully');
    } catch (error) {
      toast.error(error.message || 'Failed to add share');
    }
  };

  const handleEditShare = async () => {
    if (!editShare.member_id || !editShare.amount) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      await updateShare({
        id: parseInt(editShare.id),
        data: {
          member_id: parseInt(editShare.member_id),
          amount: parseFloat(editShare.amount),
          notes: editShare.notes
        }
      });
      setShowEditModal(false);
      setEditShare({ id: '', member_id: '', amount: '', notes: '' });
      toast.success('Share updated successfully');
    } catch (error) {
      toast.error(error.message || 'Failed to update share');
    }
  };

  const handleDeleteShare = async () => {
    if (!selectedShare) return;

    try {
      await deleteShare(selectedShare.id);
      setShowDeleteModal(false);
      setSelectedShare(null);
      toast.success('Share deleted successfully');
    } catch (error) {
      toast.error(error.message || 'Failed to delete share');
    }
  };

  const openEditModal = (share) => {
    setEditShare({
      id: share.id.toString(),
      member_id: share.member_id.toString(),
      amount: share.amount.toString(),
      notes: share.notes || ''
    });
    setShowEditModal(true);
  };

  const openDeleteModal = (share) => {
    setSelectedShare(share);
    setShowDeleteModal(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'text-yellow-600 bg-yellow-100';
      case 'approved': return 'text-green-600 bg-green-100';
      case 'rejected': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock className="w-4 h-4" />;
      case 'approved': return <CheckCircle className="w-4 h-4" />;
      case 'rejected': return <XCircle className="w-4 h-4" />;
      default: return <HelpCircle className="w-4 h-4" />;
    }
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const handleExport = (format) => {
    exportShares.mutate({
      search: searchQuery,
      status: selectedStatus !== 'all' ? selectedStatus : undefined,
      type: format,
    }, {
      onSuccess: () => {
        toast.success(`Shares exported successfully in ${format.toUpperCase()} format!`);
        setShowExportModal(false);
      },
      onError: (error) => {
        toast.error(getApiErrorMessage(error) || 'Failed to export shares');
      }
    });
  };

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Error loading shares: {error.message}</p>
          <button 
            onClick={refetch}
            className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Shares Management</h1>
          <p className="text-gray-600">Manage group shares and approvals</p>
        </div>
        <div className="flex space-x-2">
          {isAdmin && (
            <button
              onClick={() => setShowExportModal(true)}
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              <Download className="w-4 h-4 mr-2" />
              Export
            </button>
          )}
          <PermissionGate permission={PERMISSIONS.CREATE_SHARE}>
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Share
            </button>
          </PermissionGate>
        </div>
      </div>

      {/* Comprehensive Statistics Section */}
      <div className="bg-white rounded-xl shadow p-6 mb-6">
        <div className="flex items-center mb-4">
          <BarChart3 size={20} className="mr-2 text-gray-600" />
          <h3 className="text-lg font-semibold">Shares Statistics</h3>
        </div>
        {isLoadingStatistics ? (
          <div className="p-4 bg-blue-50 border border-blue-200 rounded text-blue-700">Loading statistics...</div>
        ) : statistics ? (
          <>
            {/* Main Metrics - Reduced to 4 essential cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6 kpi-grid">
              {/* Total Shares */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                <FileText size={24} className="text-blue-600 mb-2 mx-auto" />
                <p className="text-sm font-medium text-gray-700">Total Shares</p>
                <p className="text-2xl font-bold text-blue-800">{statistics.summary.total_shares}</p>
                <p className="text-xs text-gray-500">{formatCurrency(statistics.summary.total_value)} total value</p>
              </div>

              {/* Approved Shares */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                <CheckCircle size={24} className="text-green-600 mb-2 mx-auto" />
                <p className="text-sm font-medium text-gray-700">Approved Shares</p>
                <p className="text-2xl font-bold text-green-800">{statistics.by_status.approved.count}</p>
                <p className="text-xs text-gray-500">{formatCurrency(statistics.by_status.approved.total_value)} value</p>
              </div>

              {/* Pending Shares */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                <Clock size={24} className="text-yellow-600 mb-2 mx-auto" />
                <p className="text-sm font-medium text-gray-700">Pending Shares</p>
                <p className="text-2xl font-bold text-yellow-800">{statistics.by_status.pending.count}</p>
                <p className="text-xs text-gray-500">{formatCurrency(statistics.by_status.pending.total_value)} value</p>
              </div>

              {/* Rejected Shares */}
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                <XCircle size={24} className="text-red-600 mb-2 mx-auto" />
                <p className="text-sm font-medium text-gray-700">Rejected Shares</p>
                <p className="text-2xl font-bold text-red-800">{statistics.by_status.rejected.count}</p>
                <p className="text-xs text-gray-500">{formatCurrency(statistics.by_status.rejected.total_value)} value</p>
              </div>
            </div>

            {/* Top Contributors */}
            {statistics.top_members && statistics.top_members.length > 0 && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="flex items-center mb-3">
                  <Award size={18} className="text-gray-600 mr-2" />
                  <h4 className="text-md font-semibold text-gray-800">Top Contributors</h4>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {statistics.top_members.slice(0, 6).map((member, index) => (
                    <div key={member.member_id} className="bg-white rounded-lg p-3 border">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-2">
                            <span className="text-xs font-bold text-blue-600">#{index + 1}</span>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900 truncate">{member.member_name}</p>
                            <p className="text-xs text-gray-500">{member.share_count} shares</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold text-gray-900">{formatCurrency(member.total_value)}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded text-yellow-800 text-center">
            No statistics available.
          </div>
        )}
      </div>

      {/* Member Summary for non-admin */}
      {!isAdmin && memberShares && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-2">Your Shares Summary</h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <p className="text-sm text-blue-700">Total Shares</p>
              <p className="text-xl font-bold text-blue-900">{memberShares.total_shares}</p>
            </div>
            <div>
              <p className="text-sm text-blue-700">Total Value</p>
              <p className="text-xl font-bold text-blue-900">{formatCurrency(memberShares.total_value)}</p>
            </div>
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <PhoneFilters count={(searchQuery ? 1 : 0)}>
<div className="bg-white p-4 rounded-lg border mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search shares..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
            <button
              onClick={refetch}
              disabled={isLoading}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>
      </PhoneFilters>

      {/* Shares List */}
      <div className="bg-white rounded-lg border">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading shares...</p>
          </div>
        ) : filteredShares.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-gray-500">No shares found</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredShares.map((share) => (
              <div
                key={share.id}
                className="p-4 hover:bg-gray-50"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center">
                      <span className="text-sm font-medium text-gray-700">
                        {getInitials(share.member?.name || '')}
                      </span>
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">{share.member?.name}</h3>
                      <p className="text-sm text-gray-500">{share.member?.phone_number}</p>
                      <div className="flex items-center mt-1">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(share.status)}`}>
                          {getStatusIcon(share.status)}
                          <span className="ml-1 capitalize">{share.status}</span>
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="text-right">
                      <p className="text-lg font-semibold text-gray-900">
                        KSh {Number(share.amount).toLocaleString()}
                      </p>
                      <p className="text-sm text-gray-500">
                        {new Date(share.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex space-x-1">
                      <button
                        onClick={() => {
                          setSelectedShare(share);
                          setShowModal(true);
                        }}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                        title="View Details"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      {can(PERMISSIONS.EDIT_SHARE) && (
                          <button
                            onClick={() => openEditModal(share)}
                            className="p-2 text-green-600 hover:bg-green-50 rounded-lg"
                            title="Edit Share"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                      )}
                      {can(PERMISSIONS.DELETE_SHARE) && (
                          <button
                            onClick={() => openDeleteModal(share)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                            title="Delete Share"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Share Details Modal */}
      {showModal && selectedShare && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gray-300 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-lg font-medium text-gray-700">
                  {getInitials(selectedShare.member?.name || '')}
                </span>
              </div>
              <h3 className="text-lg font-bold text-gray-900">{selectedShare.member?.name}</h3>
              <p className="text-sm text-gray-500">{selectedShare.member?.phone_number}</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">
                KSh {Number(selectedShare.amount).toLocaleString()}
              </p>
              <div className="flex items-center justify-center mt-2">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedShare.status)}`}>
                  {getStatusIcon(selectedShare.status)}
                  <span className="ml-1 capitalize">{selectedShare.status}</span>
                </span>
              </div>
              <p className="text-sm text-gray-500 mt-2">
                Date: {new Date(selectedShare.created_at).toLocaleDateString()}
              </p>
              {selectedShare.notes && (
                <div className="mt-2 text-left">
                  <p className="text-sm text-gray-500">Notes:</p>
                  <p className="text-sm text-gray-700">{selectedShare.notes}</p>
                </div>
              )}
            </div>
            
            {isAdmin && selectedShare.status === 'pending' ? (
              <div className="flex gap-3">
                <button
                  onClick={() => handleStatusUpdate('approved')}
                  disabled={updateShareStatus.isPending}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  {updateShareStatus.isPending ? 'Updating...' : 'Approve'}
                </button>
                <button
                  onClick={() => handleStatusUpdate('rejected')}
                  disabled={updateShareStatus.isPending}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  {updateShareStatus.isPending ? 'Updating...' : 'Reject'}
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowModal(false)}
                className="w-full px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
              >
                Close
              </button>
            )}
          </div>
        </div>
      )}

      {/* Add Share Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Add New Share</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Member *
                </label>
                <Select
                  options={memberOptions}
                  value={memberOptions.find(option => option.value === parseInt(newShare.member_id))}
                  onChange={(selectedOption) => setNewShare({ ...newShare, member_id: selectedOption?.value?.toString() || '' })}
                  isClearable
                  placeholder="Select member..."
                  classNamePrefix="select"
                  isLoading={isLoadingMembers}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Amount (KSh) *
                </label>
                <input
                  type="number"
                  value={newShare.amount}
                  onChange={(e) => setNewShare({ ...newShare, amount: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter amount"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  value={newShare.notes}
                  onChange={(e) => setNewShare({ ...newShare, notes: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows="3"
                  placeholder="Optional notes"
                />
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setNewShare({ member_id: '', amount: '', notes: '' });
                }}
                className="flex-1 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleAddShare}
                disabled={addShare.isPending}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {addShare.isPending ? 'Adding...' : 'Add Share'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Share Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Edit Share</h3>
            
            <div className="space-y-4">
              {/* Show current member info (read-only) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Member
                </label>
                <div className="px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-900">
                  {members.find(m => m.id.toString() === editShare.member_id)?.name || 'Unknown Member'}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Amount (KSh) *
                </label>
                <input
                  type="number"
                  value={editShare.amount}
                  onChange={(e) => setEditShare({ ...editShare, amount: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter amount"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  value={editShare.notes}
                  onChange={(e) => setEditShare({ ...editShare, notes: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows="3"
                  placeholder="Optional notes"
                />
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditShare({ id: '', member_id: '', amount: '', notes: '' });
                }}
                className="flex-1 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleEditShare}
                disabled={updateShare.isPending}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {updateShare.isPending ? 'Updating...' : 'Update Share'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Share Modal */}
      {showDeleteModal && selectedShare && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trash2 className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900">Delete Share</h3>
              <p className="text-sm text-gray-500 mt-2">
                Are you sure you want to delete this share for {selectedShare.member?.name}?
              </p>
              <p className="text-lg font-semibold text-gray-900 mt-2">
                KSh {Number(selectedShare.amount).toLocaleString()}
              </p>
              <p className="text-xs text-gray-500">
                This action cannot be undone.
              </p>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteShare}
                disabled={deleteShare.isPending}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {deleteShare.isPending ? 'Deleting...' : 'Delete Share'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Export Format Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Export Format</h2>
            <p className="text-gray-600 mb-6">Choose the format for your shares export:</p>
            
            <div className="space-y-3">
              <button
                onClick={() => handleExport('csv')}
                className="w-full flex items-center justify-between border border-gray-100 dark:border-gray-700 py-2 px-4 rounded-lg hover:bg-gray-50 transition-colors"
                disabled={exportShares.isPending}
              >
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-blue-600 font-bold text-sm">CSV</span>
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">CSV (.csv)</div>
                    <div className="text-sm text-gray-500">Simple text format, compatible with most applications</div>
                  </div>
                </div>
                {exportShares.isPending && (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                )}
              </button>

              <button
                onClick={() => handleExport('excel')}
                className="w-full flex items-center justify-between border border-gray-100 dark:border-gray-700 py-2 px-4 rounded-lg hover:bg-gray-50 transition-colors"
                disabled={exportShares.isPending}
              >
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-green-600 font-bold text-lg">X</span>
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">Excel (.xlsx)</div>
                    <div className="text-sm text-gray-500">Best for data analysis and calculations</div>
                  </div>
                </div>
                {exportShares.isPending && (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-green-600"></div>
                )}
              </button>

              <button
                onClick={() => handleExport('pdf')}
                className="w-full flex items-center justify-between border border-gray-100 dark:border-gray-700 py-2 px-4 rounded-lg hover:bg-gray-50 transition-colors"
                disabled={exportShares.isPending}
              >
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center mr-3">
                    <span className="text-red-600 font-bold text-sm">PDF</span>
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">PDF (.pdf)</div>
                    <div className="text-sm text-gray-500">Professional report format, perfect for sharing</div>
                  </div>
                </div>
                {exportShares.isPending && (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-red-600"></div>
                )}
              </button>
            </div>

            <div className="flex justify-end mt-6">
              <button
                onClick={() => setShowExportModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    
  );
};

export default SharesPage; 